import { useCallback, useEffect, useState } from "react";
import { Search, Download, ChevronLeft, ChevronRight, X } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { OrderDetailSheet } from "@/components/admin/OrderDetailSheet";
import { BOOKING_STATUSES, PAYMENT_STATUSES, STATUS_STYLES, PAYMENT_STYLES, Pill, inr, fmtDate, fieldCls } from "@/components/admin/orderUtils";

const EMPTY = { q: "", payment_status: "", status: "", date_from: "", date_to: "" };
const PAGE_SIZE = 25;

const cleanParams = (f) => Object.fromEntries(Object.entries(f).filter(([, v]) => v));

export const OrdersAdmin = () => {
  const [filters, setFilters] = useState(EMPTY);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);
  const [exporting, setExporting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/orders", { params: { ...cleanParams(filters), page, page_size: PAGE_SIZE } });
      setData(data);
    } catch {
      toast.error("Could not load orders");
    } finally {
      setLoading(false);
    }
  }, [filters, page]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (search === filters.q) return;
    const t = setTimeout(() => { setPage(1); setFilters((f) => ({ ...f, q: search })); }, 350);
    return () => clearTimeout(t);
  }, [search, filters.q]);

  const setF = (k, v) => { setPage(1); setFilters((f) => ({ ...f, [k]: v })); };
  const reset = () => { setSearch(""); setPage(1); setFilters(EMPTY); };

  const exportCsv = async () => {
    setExporting(true);
    try {
      const res = await api.get("/admin/orders/export", { params: cleanParams(filters), responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `celebrai-orders-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Export failed");
    } finally {
      setExporting(false);
    }
  };

  const pages = Math.max(1, Math.ceil(data.total / PAGE_SIZE));
  const hasFilters = Object.values(filters).some(Boolean);

  return (
    <div className="space-y-5" data-testid="orders-admin">
      <div className="bg-white rounded-3xl border border-warm p-4 sm:p-5 space-y-3">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-charcoal/40" />
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by customer name, email or order ID"
              className={`${fieldCls} w-full pl-9`} data-testid="orders-search-input" />
          </div>
          <button onClick={exportCsv} disabled={exporting} data-testid="orders-export-csv-btn"
            className="pill-btn pill-btn-primary flex items-center justify-center gap-2 !py-2 text-sm">
            <Download className="w-4 h-4" /> {exporting ? "Exporting..." : "Export CSV"}
          </button>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-2">
          <select value={filters.payment_status} onChange={(e) => setF("payment_status", e.target.value)} className={fieldCls} data-testid="orders-filter-payment">
            <option value="">All payments</option>
            {PAYMENT_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <select value={filters.status} onChange={(e) => setF("status", e.target.value)} className={fieldCls} data-testid="orders-filter-status">
            <option value="">All booking statuses</option>
            {BOOKING_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <label className="flex flex-col text-[10px] uppercase tracking-wider font-semibold text-charcoal/50">From
            <input type="date" value={filters.date_from} onChange={(e) => setF("date_from", e.target.value)} className={fieldCls} data-testid="orders-filter-date-from" />
          </label>
          <label className="flex flex-col text-[10px] uppercase tracking-wider font-semibold text-charcoal/50">To
            <input type="date" value={filters.date_to} onChange={(e) => setF("date_to", e.target.value)} className={fieldCls} data-testid="orders-filter-date-to" />
          </label>
          {hasFilters && (
            <button onClick={reset} data-testid="orders-clear-filters" className="flex items-center justify-center gap-1 text-sm terracotta font-semibold col-span-2 lg:col-span-1">
              <X className="w-4 h-4" /> Clear filters
            </button>
          )}
        </div>
      </div>

      <p className="text-sm text-charcoal/60" data-testid="orders-count">{data.total} order{data.total === 1 ? "" : "s"}</p>

      <OrdersTable items={data.items} loading={loading} onOpen={setOpenId} />

      {pages > 1 && (
        <div className="flex items-center justify-center gap-3" data-testid="orders-pagination">
          <button disabled={page <= 1} onClick={() => setPage(page - 1)} className="p-2 rounded-full border border-warm bg-white disabled:opacity-40" data-testid="orders-prev-page"><ChevronLeft className="w-4 h-4" /></button>
          <span className="text-sm text-charcoal/70" data-testid="orders-page-indicator">Page {page} of {pages}</span>
          <button disabled={page >= pages} onClick={() => setPage(page + 1)} className="p-2 rounded-full border border-warm bg-white disabled:opacity-40" data-testid="orders-next-page"><ChevronRight className="w-4 h-4" /></button>
        </div>
      )}

      <OrderDetailSheet orderId={openId} onClose={() => setOpenId(null)} onChanged={load} />
    </div>
  );
};

const OrdersTable = ({ items, loading, onOpen }) => {
  if (loading) return <div className="h-40 rounded-3xl bg-white/60 border border-warm animate-pulse" />;
  if (!items.length) return <div className="text-center py-16 text-charcoal/50 bg-white rounded-3xl border border-warm" data-testid="orders-empty">No orders match these filters.</div>;
  return (
    <>
      <div className="hidden md:block bg-white rounded-3xl border border-warm overflow-hidden">
        <table className="w-full text-sm" data-testid="orders-table">
          <thead className="bg-linen text-[11px] uppercase tracking-wider text-charcoal/50">
            <tr>{["Order ID", "Customer", "Occasion", "Total", "Payment", "Status", "Date"].map((h) => <th key={h} className="text-left px-4 py-3 font-semibold">{h}</th>)}</tr>
          </thead>
          <tbody>
            {items.map((o) => (
              <tr key={o.id} onClick={() => onOpen(o.id)} data-testid={`order-row-${o.id}`}
                className="border-t border-warm/60 hover:bg-rose-50/40 cursor-pointer transition-colors">
                <td className="px-4 py-3 font-mono text-xs text-charcoal/70">{o.id}</td>
                <td className="px-4 py-3"><p className="font-semibold text-charcoal">{o.user_name || "—"}</p><p className="text-xs text-charcoal/50">{o.user_email}</p></td>
                <td className="px-4 py-3">{o.occasion}</td>
                <td className="px-4 py-3 font-semibold">{inr(o.final_amount || o.total)}{o.confirmation_flow === "whatsapp" && <span className="block text-[10px] font-semibold text-violet-600">{o.approval_status === "approved" ? "approved" : "via WhatsApp"}</span>}</td>
                <td className="px-4 py-3"><Pill value={o.payment_status} styles={PAYMENT_STYLES} /></td>
                <td className="px-4 py-3"><Pill value={o.status} styles={STATUS_STYLES} /></td>
                <td className="px-4 py-3 text-charcoal/60 whitespace-nowrap">{fmtDate(o.created_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="md:hidden space-y-3" data-testid="orders-cards">
        {items.map((o) => (
          <button key={o.id} onClick={() => onOpen(o.id)} data-testid={`order-card-${o.id}`}
            className="w-full text-left bg-white rounded-2xl border border-warm p-4 active:scale-[0.99] transition-transform">
            <div className="flex justify-between gap-2">
              <div className="min-w-0"><p className="font-semibold text-charcoal truncate">{o.user_name || "—"}</p><p className="text-xs text-charcoal/50 truncate">{o.user_email}</p></div>
              <p className="font-display text-lg text-charcoal">{inr(o.final_amount || o.total)}</p>
            </div>
            <div className="flex flex-wrap items-center gap-2 mt-3">
              <Pill value={o.status} styles={STATUS_STYLES} /><Pill value={o.payment_status} styles={PAYMENT_STYLES} />
              <span className="text-xs text-charcoal/50 ml-auto">{o.occasion} · {fmtDate(o.created_at)}</span>
            </div>
          </button>
        ))}
      </div>
    </>
  );
};
